import { OrderedMap } from 'immutable';
import { camelizeKeys } from 'humps';
import omit from 'lodash/omit';
import merge from 'lodash/merge';

import {
  ELEMENTS__CREATE,
  ELEMENTS__DELETE,
  ELEMENTS__FETCH,
  ELEMENTS__UPDATE,
  PAGES__COPY,
  PAGES__CREATE,
  PAGES__DELETE,
  PAGES__FETCH,
  PAGES__PATCH,
  PAGES__REPLACE,
  PAGES__SORT,
  PAGES__UPDATE,
  PARTICIPANT__CREATE_IMPRESSION,
  SURVEYS__FETCH_ACTIVE,
  SURVEY_EDITOR__RESET,
  USER__LOG_OFF,
} from '../constants';

import { makeColumnSort } from '../../util';

import { getBeginType, getErrorType } from '../api-client/selectors.api-client';

export const getDefaultState = () => new OrderedMap({});

function flattenElements(children, parentId, elements = []) {
  if (!children) return elements;
  children.forEach((child) => {
    const element = merge(omit(child, 'children'), { parentId: child.parentId || parentId });
    elements.push(element);
    flattenElements(child.children, child.id, elements);
  });
  return elements;
}
export function parsePageTree(page) {
  const jsPage = camelizeKeys(page);
  return {
    page: omit(jsPage, 'children'),
    elements: flattenElements(jsPage.children, jsPage.id),
  };
}

const byPosition = makeColumnSort({ sortBy: 'position', sortDirection: 'asc' });

function processResponse(state, response) {
  return response.reduce((newState, pageTree) => {
    const { page } = parsePageTree(pageTree);
    const nextPage = {
      ...page,
      impressionCreated: page.impressionCreated || false,
      reduxLastModified: Date.now(),
    };
    return newState.merge({ [page.id]: nextPage }).sort(byPosition);
  }, state);
}

const setModified = (page) => page.set('reduxLastModified', Date.now());

export default function pagesReducer(state = getDefaultState(), action) {
  switch (action.type) {
    case ELEMENTS__CREATE:
    case ELEMENTS__DELETE:
    case ELEMENTS__UPDATE:
    case ELEMENTS__FETCH:
      return state.map(setModified);
    case PAGES__FETCH:
      return processResponse(getDefaultState(), action.data);
    case PAGES__CREATE:
    case PAGES__SORT:
    case PAGES__UPDATE:
      return processResponse(state, action.data);
    case PAGES__PATCH:
      return state.mergeIn([action.page.id], action.newProps);
    case PAGES__REPLACE:
      return state.merge({ [action.page.id]: action.page });
    case PAGES__COPY:
    case PAGES__DELETE:
      return processResponse(getDefaultState(), action.data);
    case PARTICIPANT__CREATE_IMPRESSION:
      return state.setIn([action.requestData.page_id, 'impressionCreated'], true);
    case SURVEYS__FETCH_ACTIVE:
      return processResponse(state, action.data.elements);
    case SURVEY_EDITOR__RESET:
    case USER__LOG_OFF:
      return getDefaultState();
    case getBeginType(PAGES__SORT): {
      const pages = action.data.position_changes.map(({ id, change }) => {
        const page = state.get(id);
        const position = page.get('position') + change;
        return page.merge({ position }).toJS();
      });
      return processResponse(state, pages);
    }
    case getErrorType(PAGES__SORT):
      return processResponse(state, action.pages);
    default:
      return state;
  }
}
